import { EditOutlined } from "@ant-design/icons";
import { Button, Empty, Form } from "antd";
import mergeClassNames from "common/mergeClassNames";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import FormField from "components/atoms/FormField";
import FormFieldReadOnly from "components/atoms/FormFieldReadOnly";
import DataObjectRequestVerification from "containers/DataObjectRequestVerification";
import DataObjectUnobfuscation from "containers/DataObjectUnobfuscation";
import { useAppDispatch, useAppSelector } from "hooks";
import _ from "lodash";
import React from "react";
import { getUserPIIPermissionRequest } from "store/pii/actions";
import CypressTestIds from "../../../cypress/CypressTestIds";
import OkCancelConfirmationBox from "../OkCancelConfirmationBox";

interface IFormErrorField {
    errors: string[];
    name: string | string[];
    warnings: string[];
}

interface IFormError {
    errorFields: IFormErrorField[];
    outOfDate: boolean;
    values: IBasicFormDataObject;
}

// Add missing type definitions
interface IDataObjectError {
    name: string;
    errors: string;
}

interface IBasicFormDataObject {
    [key: string]: any;
}

interface IBasicFormBuilderProps {
    formDataObject?: IBasicFormDataObject;
    schemaFields: ISchemaField[];
    readOnly?: boolean;
    showHeader?: boolean;
    requestVerification?: boolean;
    showFooter?: boolean;
    showRevealButton?: boolean;
    onCreate?: (updatedObject: IBasicFormDataObject) => void;
    onUpdate?: (id: string, updatedObject: IBasicFormDataObject) => void;
    onCancel?: () => void;
    onValidationErrors?: (validatedObject: IBasicFormDataObject, errors: IDataObjectError[]) => void;
    onTriggerEdit?: () => void;
    fromhyperlink?: string;
    contentClassName?: string;
}

interface IFormDataObjectErrorDict {
    [key: string]: IValidationError;
}

const BasicFormBuilder = React.forwardRef<{ submit: () => Promise<void> } | undefined, IBasicFormBuilderProps>(
    (
        {
            formDataObject,
            readOnly = true,
            schemaFields,
            onCreate,
            onUpdate,
            onCancel,
            onValidationErrors,
            showHeader = true,
            requestVerification = false,
            showRevealButton = false,
            showFooter = true,
            fromhyperlink,
            contentClassName = "",
            onTriggerEdit,
        }: IBasicFormBuilderProps,
        ref,
    ) => {
        const [formDataObjectErrors, setFormDataObjectErrors] = React.useState<IFormDataObjectErrorDict>({});
        const [showFormSaveWarning, setShowFormSaveWarning] = React.useState<boolean>(false);
        const [showFormWarning, setShowFormWarning] = React.useState<boolean>(false);
        const [formWarningMessage, setFormWarningMessage] = React.useState<string>("Empty form cannot be saved.");
        const [updatedDataObject, setUpdatedDataObject] = React.useState<IBasicFormDataObject>({});
        const [form] = Form.useForm();
        const [editedValues, setEditedValues] = React.useState<IBasicFormDataObject>({});
        const [canRevealPII, setCanRevealPII] = React.useState<boolean>(false);
        const formDataObjectRef = React.useRef<IBasicFormDataObject | undefined>(undefined);
        const dispatch = useAppDispatch();

        const hasPIIFields = React.useMemo(() => schemaFields.some((field) => field.isPII), [schemaFields]);

        const piiPermission = useAppSelector((state) => state.pii?.permission);

        React.useEffect(() => {
            if (piiPermission?.canView) {
                setCanRevealPII(true);
            } else {
                setCanRevealPII(false);
            }
        }, [piiPermission]);

        React.useEffect(() => {
            if (formDataObject?.errors && Array.isArray(formDataObject.errors)) {
                const initialErrors: IFormDataObjectErrorDict = {};
                (formDataObject.errors as IDataObjectError[]).forEach((error) => {
                    if (error.name) {
                        const currentValue = (formDataObject as Record<string, unknown>)[error.name];
                        const isEmptyValue = currentValue === undefined || currentValue === null || (typeof currentValue === "string" && currentValue.trim() === "");
                        if (!isEmptyValue) {
                            return;
                        }
                        initialErrors[error.name] = {
                            help: error.errors ?? "",
                            validateStatus: "error",
                        };
                    }
                });
                setFormDataObjectErrors(initialErrors);
            }
        }, [formDataObject]);

        React.useEffect(() => {
            if (formDataObject) {
                formDataObjectRef.current = formDataObject;
                setEditedValues({});
                setUpdatedDataObject({});
                form.resetFields();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formDataObject?.version, formDataObject?.dataObjectId]);

        React.useLayoutEffect(() => {
            dispatch(getUserPIIPermissionRequest());
        }, [dispatch]);

        React.useImperativeHandle(ref, () => ({
            async submit() {
                return await onSubmitForm();
            },
        }));

        const onFormValuesChanged = React.useCallback((changedValues: IBasicFormDataObject, allValues: IBasicFormDataObject) => {
            if (changedValues && _.isObject(changedValues)) {
                setEditedValues((prev) => {
                    const updated = { ...prev, ...changedValues };

                    // If there's no original data, all changes are edits
                    const originalData = formDataObjectRef.current;
                    if (!originalData) {
                        return updated;
                    }

                    // Remove keys that have been reverted to their original values
                    return Object.keys(updated).reduce((acc, key) => {
                        const currentValue = allValues[key];
                        const originalValue = originalData[key];
                        const keyExistsInOriginal = key in originalData;

                        // Include the key if:
                        // 1. It's a new field not in original data, OR
                        // 2. The value differs from the original
                        if (!keyExistsInOriginal || !_.isEqual(currentValue, originalValue)) {
                            acc[key] = currentValue;
                        }
                        return acc;
                    }, {} as IBasicFormDataObject);
                });
                setFormDataObjectErrors((prev) => {
                    let errorObject = { ...prev };
                    for (let key in changedValues) {
                        delete errorObject[key];
                    }
                    return errorObject;
                });
            }
            setUpdatedDataObject(allValues);
        }, []); // Ref intentionally omitted: captures baseline values from form load time to detect reverts

        const onWarningMessageCancel = React.useCallback(() => {
            setShowFormSaveWarning(false);
        }, []);

        const onWarningMessageConfirmed = React.useCallback(() => {
            if (Object.keys(updatedDataObject).length > 0) {
                if (!formDataObject?.dataObjectId) {
                    onCreate && onCreate(updatedDataObject);
                } else {
                    onUpdate && onUpdate(formDataObject.dataObjectId, updatedDataObject);
                }
            }
            setShowFormSaveWarning(false);
            setUpdatedDataObject({});
        }, [formDataObject, onCreate, onUpdate, updatedDataObject]);

        const isEmptyFormDataObject = React.useCallback((): string | null => {
            if (readOnly) {
                return "Read-only access, cannot save form.";
            }

            if (Object.keys(editedValues).length === 0 || Object.keys(updatedDataObject).length === 0) {
                return "No change in any input.";
            }

            if (!form.isFieldsTouched()) {
                return "No change in any input.";
            }

            let values = [];
            for (let key in updatedDataObject) {
                if (key !== "errors") {
                    values.push(updatedDataObject[key]);
                }
            }

            const nonEmptyDataObject = _.some(values, (value) => {
                if (value === null || value === undefined) return false;
                if (typeof value === "string") return value.trim() !== "";
                if (typeof value === "number" || typeof value === "boolean") return true;
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === "object") return Object.keys(value).length > 0;
                return false;
            });
            return !nonEmptyDataObject ? "Empty form cannot be saved." : null;
        }, [editedValues, form, readOnly, updatedDataObject]);

        const handleUnobfuscationSave = React.useCallback(
            (payload: any) => {
                if (formDataObject?.dataObjectId && payload) {
                    onUpdate && onUpdate(formDataObject.dataObjectId, payload);
                }

                const newFormData = { ...formDataObject, ...payload };
                form.setFieldsValue(newFormData);
            },
            [form, formDataObject, onUpdate],
        );

        const getFormErrors = React.useCallback(
            (error: IFormError | null) => {
                let validationErrors: IDataObjectError[] = [];

                error?.errorFields?.forEach(({ name, errors }) => {
                    const nameArray = Array.isArray(name) ? name : [name];
                    const errorKey = nameArray.join("");
                    validationErrors.push({
                        name: errorKey,
                        errors: Array.isArray(errors) ? errors.join("\n") : errors,
                    });
                });

                if (onValidationErrors && formDataObject) {
                    onValidationErrors(formDataObject, validationErrors);
                }
                return validationErrors;
            },
            [formDataObject, onValidationErrors],
        );

        const onSubmitForm = React.useCallback(async () => {
            const warningMessage = isEmptyFormDataObject();
            if (warningMessage) {
                setFormWarningMessage(warningMessage);
                setShowFormWarning(true);
                return;
            }

            let formError: IFormError | null = null;

            try {
                // Only validate fields that were actually edited to avoid validating masked PII fields
                const editedFieldNames = Object.keys(editedValues);
                if (editedFieldNames.length > 0 && formDataObject?.dataObjectId) {
                    // For updates, only validate edited fields
                    await form.validateFields(editedFieldNames);
                } else {
                    // For new records, validate all fields
                    await form.validateFields();
                }
            } catch (errorInfo) {
                const error = errorInfo as unknown as IFormError;
                formError = error;
            }

            const validationErrors = getFormErrors(formError);

            const formEdits = form.getFieldsValue(true);
            const newDataObject = _.cloneDeep(formEdits);
            if (formDataObject?.version !== undefined && formDataObject?.version !== null) {
                newDataObject["version"] = formDataObject?.version;
            }

            const originalErrors = Array.isArray(formDataObject?.errors) ? (formDataObject.errors as IDataObjectError[]) : [];
            // Retain only the backend errors that were NOT edited by the user (still pending)
            const pendingErrors = originalErrors.filter((e) => !(e.name in editedValues));
            const combinedErrors = [...validationErrors, ...pendingErrors];

            if (validationErrors.length > 0) {
                setShowFormSaveWarning(true);
                setUpdatedDataObject({ ...editedValues, version: formDataObject?.version, errors: combinedErrors });
            } else {
                if (!formDataObject) {
                    onCreate && onCreate({ ...newDataObject, errors: [] });
                } else {
                    const payload: IBasicFormDataObject = { ...editedValues, version: formDataObject.version, errors: combinedErrors };
                    onUpdate && onUpdate(formDataObject.dataObjectId, payload);
                }
            }
        }, [editedValues, form, formDataObject, getFormErrors, isEmptyFormDataObject, onCreate, onUpdate]);

        return (
            <>
                <OkCancelConfirmationBox
                    show={showFormSaveWarning}
                    message="Do you still want to save this record with warnings?"
                    onCancel={onWarningMessageCancel}
                    onOk={onWarningMessageConfirmed}
                ></OkCancelConfirmationBox>
                <OkCancelConfirmationBox show={showFormWarning} message={formWarningMessage} onOk={() => setShowFormWarning(false)}></OkCancelConfirmationBox>
                <FlexContainerLayout
                    containerClassName="gap-0"
                    headerClassName="px-4 py-3"
                    contentClassName={mergeClassNames("px-4 py-1", contentClassName)}
                    footerClassName="px-4 py-3 bg-slate-100"
                    showHeader={showHeader}
                    header={
                        <h5 className="w-full">
                            {formDataObject
                                ? readOnly
                                    ? "Display Dataset Record"
                                    : formDataObject.dataObjectId
                                      ? "Update Dataset Record"
                                      : "Add Dataset Record"
                                : "Dataset Record"}
                        </h5>
                    }
                    showFooter={showFooter}
                    footer={
                        <div className="flex w-full flex-wrap justify-end gap-2">
                            {onTriggerEdit && readOnly && (
                                <Button onClick={onTriggerEdit} icon={<EditOutlined />} type="primary">
                                    Edit
                                </Button>
                            )}
                            <div className="flex-1" />
                            {showRevealButton && hasPIIFields && canRevealPII && (
                                <DataObjectUnobfuscation schemaFields={schemaFields} selectedDataObject={formDataObject} onSave={handleUnobfuscationSave} readOnly={readOnly} />
                            )}
                            <Button onClick={() => onCancel && onCancel()}>{fromhyperlink || readOnly ? "Close" : "Cancel"}</Button>
                            {!readOnly && (
                                <Button type="primary" onClick={onSubmitForm}>
                                    Save
                                </Button>
                            )}
                            {requestVerification && formDataObject?.dataObjectId && (
                                <DataObjectRequestVerification
                                    key={formDataObject.dataObjectId}
                                    dataObject={formDataObject}
                                    schemaFields={schemaFields}
                                ></DataObjectRequestVerification>
                            )}
                        </div>
                    }
                >
                    {readOnly && !formDataObject ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Dataset record selected" />
                    ) : (
                        <Form
                            data-testid={CypressTestIds.DATASET_RECORD_FORM}
                            form={form}
                            name="basic"
                            initialValues={formDataObject}
                            layout="vertical"
                            className="basic-form-builder"
                            onValuesChange={onFormValuesChanged}
                            validateTrigger="onBlur"
                        >
                            {schemaFields?.map((field) =>
                                readOnly ? (
                                    <FormFieldReadOnly
                                        key={field.fieldId}
                                        name={field.name}
                                        classifiers={field.classifiers}
                                        isPII={field.isPII}
                                        description={field.description}
                                        isRequired={field.isRequired}
                                        dataType={field.dataType}
                                        fieldError={formDataObjectErrors[field.fieldId] || undefined}
                                    >
                                        {formDataObject ? formDataObject[field.fieldId] : undefined}
                                    </FormFieldReadOnly>
                                ) : (
                                    <FormField
                                        fieldErrors={formDataObjectErrors[field.fieldId] || {}}
                                        key={field.fieldId}
                                        verificationResult={formDataObject?.verificationResults?.[field.fieldId]}
                                        {...field}
                                    />
                                ),
                            )}
                        </Form>
                    )}
                </FlexContainerLayout>
            </>
        );
    },
);

export default BasicFormBuilder;
